import { Children, ForwardedRef, KeyboardEvent, MouseEvent, forwardRef, isValidElement, useCallback, useMemo } from "react";
import { SelectInstance } from "react-select";

import { Badge, Button, Buttons, LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { Body } from "capitalrx-components-2.0/lib/typography";

import { useId } from "../../hooks/useId";
import { EmptyArray } from "../../utilities/EmptyArray";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Dropdown } from "../Dropdown";
import { MultiSelectionProps } from "../Dropdown/MultiSelectionProps";
import { SharedDropdown } from "../Dropdown/SharedDropdown";
import { flattenItems } from "../Dropdown/helpers";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { InputContainer } from "../shared";
import { Group } from "./Group";
import { MultiSelectDropdownProps } from "./MultiSelectDropdown";
import { Option, OptionProps } from "./Option";
import { OptionItem } from "./OptionItem";
import { Placeholder } from "./Placeholder";
import { getItems } from "./helpers";

import styles from "./styles.module.scss";

/**
 * The `Input` component properties.
 */
export interface InputProps extends MultiSelectDropdownProps, MultiSelectionProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * Called when the input loses focus and should validate.
   */
  readonly onFocusExit?: () => void;
}

export const Input = forwardRef(function Input(props: InputProps, ref: ForwardedRef<SelectInstance<OptionItem | null, boolean>>) {
  const {
    label,
    tooltip,
    instructions,
    defaultValue = null,
    children,
    hideInstructions,
    hideLabel,
    placeholder = "Select",
    required = false,
    searchable,
    enableCheckboxOptions = true,
    onChange,
    onSearchChange,
    onFocusExit,
  } = props;

  const { value = defaultValue } = props;

  const id = useId();
  const selectedValues = useMemo(() => new Set(value ?? EmptyArray), [value]);

  const items = getItems(children);
  const flattenedItems = flattenItems(items);

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.MultiSelectDropdown, "multi-select-dropdown"],
    testIdPrefix: "multi-select-dropdown",
  });

  const changeHandler = useCallback(
    (selectedValue: string | number | null) => {
      if (selectedValue === null) return;
      if (value?.includes(selectedValue)) return;

      if (disabled) return;
      if (readOnly || submitting) return;

      if (selectedValues.has(selectedValue)) {
        onChange?.(
          (value ?? EmptyArray).filter((item) => item !== selectedValue),
          value,
        );
      } else {
        onChange?.([...(value ?? EmptyArray), selectedValue], value);
      }
      onFocusExit?.();
    },
    [value, disabled, submitting, readOnly, onChange, onFocusExit, selectedValues],
  );

  const clearKeyBoardHandler = useCallback(
    (e?: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      if (e?.key === "Enter" || e?.key === " ") {
        e.preventDefault();
        onChange?.(EmptyArray, value);
        onFocusExit?.();
      }
    },
    [value, disabled, submitting, readOnly, onChange, onFocusExit],
  );

  const clearClickHandler = useCallback(
    (e?: MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();

      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(EmptyArray, value);
      onFocusExit?.();
    },
    [value, disabled, submitting, readOnly, onChange, onFocusExit],
  );

  const filteredChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;

    if (child.type === Option) {
      const { props } = child;
      const { value, label, children } = props;

      return (
        <Dropdown.Option value={value} label={label}>
          {children}
        </Dropdown.Option>
      );
    } else if (child.type === Group) {
      const { children } = child.props;
      const newChildren = Children.map(children, (child) => {
        if (!isValidElement(child)) return null;
        if (child.type === Option) {
          const { props } = child;
          const { value, label, children } = props as OptionProps;

          return (
            <Dropdown.Option value={value} label={label}>
              {children}
            </Dropdown.Option>
          );
        }
        return null;
      });

      return <Dropdown.Group {...child.props}>{newChildren}</Dropdown.Group>;
    }

    return null;
  });

  const removeClickHandler = useCallback(
    (removeValue: string | number | undefined) => {
      if (removeValue === undefined || !value?.includes(removeValue)) return;

      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(
        (value ?? EmptyArray).filter((item) => item !== removeValue),
        value,
      );
      onFocusExit?.();
    },
    [value, disabled, readOnly, submitting, onChange, onFocusExit],
  );

  const hasSelectedValues = useMemo(() => {
    return flattenedItems.some((item) => selectedValues.has(item.value));
  }, [flattenedItems, selectedValues]);

  const itemByValue = useMemo(() => new Map(flattenedItems.map((i) => [i.value, i])), [flattenedItems]);

  const handleSelectAll = useCallback(() => {
    if (disabled || readOnly || submitting) return;

    const allValues = flattenedItems.map((item) => item.value);
    const allSelected = allValues.every((val) => selectedValues.has(val));

    if (allSelected) {
      onChange?.([], value);
    } else {
      onChange?.(allValues, value);
    }
    onFocusExit?.();
  }, [flattenedItems, selectedValues, disabled, readOnly, submitting, onChange, value, onFocusExit]);

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={instructions}
        disabled={disabled}
        readOnly={readOnly}
        tooltip={tooltip}
        required={required}
        hideInstructions={hideInstructions}
        hideLabel={hideLabel}
      >
        <SharedDropdown
          ref={ref}
          id={id}
          value={null}
          label={label}
          onChange={changeHandler}
          searchable={searchable}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          enableCheckboxOptions={enableCheckboxOptions}
          selectedValues={selectedValues}
          onSelectAll={handleSelectAll}
          onSearchChange={onSearchChange}
          onRemove={removeClickHandler}
          isItemSelected={(optionValue) => selectedValues.has(optionValue)}
          onFocusExit={onFocusExit}
          aria-label={props["aria-label"]}
        >
          {filteredChildren}
        </SharedDropdown>
        {hasSelectedValues && (
          <>
            <div className="badge-container" role="list" aria-label="Selected Values" aria-live="polite" aria-relevant="additions removals">
              {(value ?? EmptyArray).map((i) => {
                const item = itemByValue.get(i);
                if (!item || !selectedValues.has(item.value)) return null;
                const { value, label, tagType, tagInverted } = item;

                return (
                  <div key={value} role="listitem">
                    <Badge
                      disabled={readOnly || submitting || disabled}
                      buttonTooltip="Remove"
                      type={tagType}
                      inverted={tagInverted}
                      onClick={() => removeClickHandler(value)}
                    >
                      {label}
                    </Badge>
                  </div>
                );
              })}
            </div>
            {!readOnly && !disabled && (
              <Body className="clear-container" size="mini">
                <Button
                  appearance={Buttons.Functional.Tertiary.Compact}
                  className="clear-button"
                  aria-label="Deselects all items"
                  onClick={clearClickHandler}
                  onKeyPress={clearKeyBoardHandler}
                >
                  Clear all
                </Button>
              </Body>
            )}
          </>
        )}
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
